import React, { useState, useEffect, useCallback } from 'react';
import { getAdminProfile, updateAdminProfile, changeAdminPassword } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    email: '',
    username: '',
    name: ''
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await getAdminProfile(token);
      setProfileData({
        email: data.email,
        username: data.username,
        name: data.name
      });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || t('admin.profile.profileUpdateError') });
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!token) return;

    try {
      await updateAdminProfile({ email: profileData.email, name: profileData.name }, token);
      setMessage({ type: 'success', text: t('admin.profile.profileUpdateSuccess') });
      fetchProfile();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || t('admin.profile.profileUpdateError') });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('admin.profile.passwordMismatch') });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: t('admin.profile.passwordTooShort') });
      return;
    }

    if (!token) return;

    try {
      await changeAdminPassword(
        { oldPassword: passwordData.oldPassword, newPassword: passwordData.newPassword },
        token
      );
      setPasswordMessage({ type: 'success', text: t('admin.profile.passwordUpdateSuccess') });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setPasswordMessage({ type: 'error', text: err.response?.data?.message || t('admin.profile.passwordUpdateError') });
    }
  };

  if (loading) {
    return <div className="profile-page"><p>{t('admin.profile.loading')}</p></div>;
  }

  return (
    <div className="profile-page">
      <h1>{t('admin.profile.title')}</h1>

      <div className="profile-section">
        <h2>{t('admin.profile.profileInfo')}</h2>
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
        <form onSubmit={handleProfileSubmit}>
          <div className="form-group">
            <label htmlFor="username">{t('admin.profile.username')}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={profileData.username}
              disabled
              className="disabled-input"
            />
            <small>{t('admin.profile.usernameNote')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('admin.profile.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={profileData.email}
              onChange={handleProfileChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">{t('admin.profile.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={profileData.name}
              onChange={handleProfileChange}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary">
            {t('admin.profile.updateProfile')}
          </button>
        </form>
      </div>

      <div className="profile-section">
        <h2>{t('admin.profile.changePassword')}</h2>
        {passwordMessage && (
          <div className={`message ${passwordMessage.type}`}>
            {passwordMessage.text}
          </div>
        )}
        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label htmlFor="oldPassword">{t('admin.profile.currentPassword')}</label>
            <input
              type="password"
              id="oldPassword"
              name="oldPassword"
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">{t('admin.profile.newPassword')}</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('admin.profile.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-primary">
            {t('admin.profile.updatePassword')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
